import { useCallback, useEffect, useRef, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { useSession } from "../../auth/session"
import Api from "../../api/api"
import { meSelector } from "../../redux/globalSlice"
import { Organization, OrgRole, orgRoleLabels, UserRole, UserSearchResult } from "../../api/models"
import BetaTestersCard from "../BetaTestersCard"
import { ImageFieldView, useImageField } from "../ImageField"
import UserFilter from "../widgets/UserFilter"
import UserChip from "../widgets/UserChip"
import ErrorDialog from "../widgets/ErrorDialog"
import Spinner from "../widgets/Spinner"
import { sameUserId } from "../../utils/userIds"
import { backdropDismiss } from "../../utils/backdropDismiss"

// Edit the org's display face — name, website URL, logo. The @handle is
// permanent and not on offer here. Vendor admins and system admins.
function EditOrganizationDialog({ org, accessToken, onClose, onSaved }: {
  org: Organization
  accessToken: string
  onClose: () => void
  onSaved: (organization: Organization) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState(org.name)
  const [websiteUrl, setWebsiteUrl] = useState(org.websiteUrl ?? "")
  const [imageRemoved, setImageRemoved] = useState(false)
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  const logo = useImageField(accessToken, (width) =>
    Api.createOrganizationImageUpload({ orgId: org.id, width, accessToken }))

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "saving"
  const formValid = name.trim() !== ""
    && (websiteUrl.trim() === "" || websiteUrl.trim().startsWith("https://"))

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      let imageWidth: number | null | undefined
      let imageRev: string | undefined
      if (logo.file !== null) {
        const uploaded = await logo.upload("")
        imageWidth = uploaded.width
        imageRev = uploaded.rev
      } else if (imageRemoved)
        imageWidth = null
      const { organization } = await Api.updateOrganization({
        orgId: org.id,
        name: name.trim(),
        websiteUrl: websiteUrl.trim() !== "" ? websiteUrl.trim() : null,
        imageWidth, imageRev,
        accessToken
      })
      onSaved(organization)
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Edit Organization</h2>

        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Display name</span>
            <input
              type="text"
              className="generate-codes-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              maxLength={255}
              autoFocus
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Website URL (optional)</span>
            <input
              type="text"
              className="generate-codes-input"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              disabled={busy}
              maxLength={2048}
              placeholder="https://www.newfangledaudio.com"
            />
            {websiteUrl.trim() !== "" && !websiteUrl.trim().startsWith("https://") && (
              <span className="generate-codes-error">Must start with https://</span>
            )}
          </label>

          <ImageFieldView
            fields={logo}
            busy={busy}
            label="Logo (PNG, optional)"
            current={{
              imageUrl: org.imageUrl ?? undefined,
              removed: imageRemoved,
              onRemove: () => setImageRemoved(true),
              onRestore: () => setImageRemoved(false)
            }}
          />
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={!formValid || busy}
            onClick={handleSubmit}
          >
            {busy ? <><Spinner size={12} /> Saving…</> : "Save"}
          </button>
        </div>
      </div>
    </dialog>
  )
}

// Adds an existing user to the workforce — the house "add" pattern: a
// conditionally mounted modal opened from the section header.
function AddMemberDialog({ orgId, existingIds, accessToken, onClose, onAdded }: {
  orgId: string
  existingIds: string[]
  accessToken: string
  onClose: () => void
  onAdded: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [adding, setAdding] = useState<UserSearchResult | null>(null)
  const [orgRole, setOrgRole] = useState<OrgRole>(OrgRole.Developer)
  const [step, setStep] = useState<"form" | "saving" | "success" | "error">("form")
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "saving"

  const handleSubmit = async () => {
    if (adding === null) return
    setStep("saving")
    setErrorMessage("")
    try {
      // the backend deposits alpha licenses for the org's versions currently
      // in team testing — report what landed (the AddTesterDialog pattern)
      const { reconciles } = await Api.addOrganizationMember({ orgId, userId: adding.id, orgRole, accessToken })
      const deposited = reconciles.reduce((acc, r) => acc + r.depositedCount, 0)
      setSuccessMessage(deposited > 0
        ? `Added — deposited ${deposited} alpha license${deposited === 1 ? "" : "s"} for the versions in testing.`
        : "Added.")
      onAdded()
      setStep("success")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog redeem-dialog-overflow"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Add Member</h2>

        {step === "success" ? (
          <>
            <p className="generate-codes-success">{successMessage}</p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div className="generate-codes-form">
              <div className="generate-codes-field">
                <span className="generate-codes-label">User</span>
                <UserFilter
                  selected={adding}
                  onSelect={setAdding}
                  accessToken={accessToken}
                  placeholder="ID, name or email…"
                  disabledIds={existingIds}
                  disabledHint="already a member"
                />
              </div>
              <div className="generate-codes-field">
                <span className="generate-codes-label">Role</span>
                <select
                  className="generate-codes-input"
                  style={{ width: "auto" }}
                  value={orgRole}
                  disabled={busy}
                  onChange={(e) => setOrgRole(e.target.value as OrgRole)}
                >
                  <option value={OrgRole.Developer}>{orgRoleLabels[OrgRole.Developer]}</option>
                  <option value={OrgRole.Admin}>{orgRoleLabels[OrgRole.Admin]}</option>
                </select>
                <span className="generate-codes-hint">
                  Admins manage the workforce, offerings, codes and grants;
                  developers manage listings, versions and betas. New members
                  receive alpha licenses for versions currently in testing.
                </span>
              </div>
            </div>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" disabled={adding === null || busy} onClick={handleSubmit}>
                {busy ? <><Spinner size={12} /> Adding…</> : "Add"}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}

// The workforce card: members with role management (add via the header's
// modal, role select, remove — the last vendor admin can't be
// demoted/removed, server-guarded and surfaced here). Vendor-admin/system-
// admin only; vendor developers see a note instead. Role ASSIGNMENT is org
// self-governance: only the org's own admin members get the selects (system
// admins see static labels — the backend refuses them too), and nobody gets
// one on their own row (no self-promotion or self-demotion).
function MembersCard({ orgId, canManage, canAssignRoles, myUserId, accessToken }: {
  orgId: string
  canManage: boolean
  canAssignRoles: boolean
  myUserId: string
  accessToken: string
}) {
  const [members, setMembers] = useState<{ userId: string, orgRole: OrgRole, createdAt: string, user: UserSearchResult }[] | undefined>(undefined)
  const [membersError, setMembersError] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [busyUserId, setBusyUserId] = useState<string | null>(null)
  // two-click confirm: first click arms this member's Remove button (removal
  // also revokes their alpha licenses in running alphas/betas)
  const [armedId, setArmedId] = useState<string | null>(null)
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")

  const loadMembers = useCallback(async () => {
    if (!canManage) return
    setMembersError(false)
    try {
      const { members } = await Api.listOrganizationMembers({ orgId, accessToken })
      setMembers(members)
    } catch {
      setMembersError(true)
    }
  }, [orgId, accessToken, canManage])

  useEffect(() => {
    setMembers(undefined)
    loadMembers()
  }, [loadMembers])

  const handleRoleChange = async (userId: string, orgRole: OrgRole) => {
    setBusyUserId(userId)
    setActionError("")
    try {
      await Api.updateOrganizationMember({ orgId, userId, orgRole, accessToken })
      await loadMembers()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "unknown error")
      await loadMembers()   // restore the select to the server truth
    } finally {
      setBusyUserId(null)
    }
  }

  const handleRemove = async (userId: string) => {
    if (armedId !== userId) {
      setArmedId(userId)
      return
    }
    setArmedId(null)
    setBusyUserId(userId)
    setActionError("")
    setNotice("")
    try {
      const { revokedCount } = await Api.removeOrganizationMember({ orgId, userId, accessToken })
      setNotice(revokedCount > 0
        ? `Removed — revoked ${revokedCount} alpha license${revokedCount === 1 ? "" : "s"}.`
        : "Removed.")
      await loadMembers()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "unknown error")
    } finally {
      setBusyUserId(null)
    }
  }


  return (
    <div className="tab-card" style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 className="section-title tab-card-tab">Members</h2>
        {canManage && (
          <button
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
            onClick={() => setAddOpen(true)}
          >
            Add member
          </button>
        )}
      </div>
      <div className="tab-card-body">
        {!canManage ? (
          <p className="empty-state">Members are managed by the organization's admins.</p>
        ) : (
          <>
            {notice && <p style={{ color: "var(--nfa-blue)", fontSize: "10pt" }}>{notice}</p>}

            {membersError && members === undefined ? (
              <p className="empty-state">Error loading members</p>
            ) : members === undefined ? (
              <p className="empty-state"><Spinner size={16} /> Loading members…</p>
            ) : (
              members.map((member) => (
                <div key={member.userId} className="license-group">
                  <div className="product-group-header">
                    <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <UserChip userId={member.userId} user={member.user} />
                      {member.user.enabled === false && <span className="badge">Disabled</span>}
                    </h2>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
                      {member.user.email && (
                        <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>{member.user.email}</span>
                      )}
                      {canAssignRoles && !sameUserId(member.userId, myUserId) ? (
                        <select
                          className="generate-codes-input"
                          style={{ width: "auto", fontSize: "10pt" }}
                          value={member.orgRole}
                          disabled={busyUserId === member.userId}
                          onChange={(e) => handleRoleChange(member.userId, e.target.value as OrgRole)}
                        >
                          <option value={OrgRole.Developer}>{orgRoleLabels[OrgRole.Developer]}</option>
                          <option value={OrgRole.Admin}>{orgRoleLabels[OrgRole.Admin]}</option>
                        </select>
                      ) : (
                        <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
                          {orgRoleLabels[member.orgRole]}
                        </span>
                      )}
                      {/* never on one's own row — the backend refuses
                          self-removal too. Two-click armed confirm: removal
                          also revokes running alpha licenses. */}
                      {!sameUserId(member.userId, myUserId) && (
                        <button
                          className="card-link-danger"
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt", display: "inline-grid", fontWeight: armedId === member.userId ? "bold" : undefined }}
                          onClick={() => handleRemove(member.userId)}
                          onBlur={() => setArmedId((prev) => prev === member.userId ? null : prev)}
                          disabled={busyUserId === member.userId}
                        >
                          {/* the label always occupies the cell so the button
                              keeps its width; the spinner overlays it */}
                          <span style={{ gridArea: "1 / 1", visibility: busyUserId === member.userId ? "hidden" : "visible" }}>
                            {armedId === member.userId ? "Remove & revoke alpha licenses?" : "Remove"}
                          </span>
                          {busyUserId === member.userId && (
                            <span style={{ gridArea: "1 / 1", display: "inline-flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true">
                              <Spinner size={12} />
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {actionError && (
        <ErrorDialog message={actionError} onClose={() => setActionError("")} />
      )}

      {addOpen && (
        <AddMemberDialog
          orgId={orgId}
          existingIds={(members ?? []).map(m => m.userId)}
          accessToken={accessToken}
          onClose={() => setAddOpen(false)}
          onAdded={loadMembers}
        />
      )}
    </div>
  )
}

// One organization's page (/organizations/:id): display name, logo and
// website with an Edit dialog (vendor admins and system admins — the
// @handle is permanent), the workforce card, and the beta-tester audience
// card (any member). The SAME page serves the org's own people and system
// admins managing any vendor — the URL carries the scope, the server
// enforces the powers. Client-side guard only for chrome: non-members who
// aren't system admins bounce to the index.
export default function OrganizationPage() {
  const { id } = useParams<{ id: string }>()
  const orgId = id!
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const me = useSelector(meSelector)?.data
  const isSystemAdmin = me?.role === UserRole.Admin
  const membership = me?.memberships?.find(m => m.orgId === orgId)

  // the full record (name, website, logo) comes from the org listing —
  // memberships only carry the name
  const [org, setOrg] = useState<Organization | undefined>(undefined)
  const [editOpen, setEditOpen] = useState(false)

  useEffect(() => {
    if (!accessToken) return
    let stale = false
    Api.listOrganizations({ accessToken })
      .then(({ organizations }) => {
        if (!stale) setOrg(organizations.find(o => o.id === orgId))
      })
      .catch(() => { })
    return () => { stale = true }
  }, [orgId, accessToken])

  if (me === undefined)
    return <div className="page"><Spinner size={24} /></div>
  if (!isSystemAdmin && membership === undefined)
    return <Navigate to="/organizations" replace />

  const canManage = isSystemAdmin || membership?.orgRole === OrgRole.Admin
  const name = org?.name ?? membership?.orgName

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <h1 className="page-title" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
          {org?.imageUrl && (
            <img src={org.imageUrl} alt="" style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 4 }} />
          )}
          {name ?? <Spinner size={16} />}
        </h1>
        {canManage && (
          <button
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
            onClick={() => setEditOpen(true)}
            disabled={org === undefined}
          >
            Edit
          </button>
        )}
      </div>
      {(membership !== undefined || org?.websiteUrl) && (
        <p style={{ color: "var(--nfa-light-grey)", fontSize: "10pt", marginTop: 0, display: "flex", gap: 12, flexWrap: "wrap" }}>
          {org?.websiteUrl && (
            <a href={org.websiteUrl} target="_blank" rel="noreferrer" className="card-link">
              {org.websiteUrl.replace(/^https:\/\//, "")}
            </a>
          )}
          {membership !== undefined && (
            <span>You are {membership.orgRole === OrgRole.Admin ? "an admin" : "a developer"} of this organization.</span>
          )}
        </p>
      )}

      <MembersCard
        orgId={orgId}
        canManage={canManage}
        canAssignRoles={membership?.orgRole === OrgRole.Admin}
        myUserId={me.id}
        accessToken={accessToken}
      />
      {/* tester management is org-admin-scoped like member management —
          the server 403s developer-role members, so don't mount for them */}
      {canManage && <BetaTestersCard orgId={orgId} accessToken={accessToken} />}

      {editOpen && org !== undefined && (
        <EditOrganizationDialog
          org={org}
          accessToken={accessToken}
          onClose={() => setEditOpen(false)}
          onSaved={(organization) => setOrg(organization)}
        />
      )}
    </div>
  )
}
